import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { AdminRoleSelector } from '@client/components/_shared/selectors/users/AdminRoleSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import type { AdminRole } from '@shared/constants/business/user';
import { createFileRoute } from '@tanstack/react-router';
import { Avatar, Drawer, Skeleton, Space, Typography } from 'antd';
import { ShieldCheck, User } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/admin-console/user-access/admin-users/edit/$userId')({
    params: {
        parse({ userId }) {
            return { userId: BigInt(userId) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
    component: EditAdminRolesRoute,
});

function EditAdminRolesRoute() {
    const { userId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [selectedRoles, setSelectedRoles] = useState<AdminRole[]>([]);

    const {
        data: user,
        isLoading,
        error,
    } = trpc.admin.adminRole.get.useQuery({ userId }, { refetchOnWindowFocus: false });

    const { mutate: updateRoles, isPending: isUpdating } = trpc.admin.adminRole.updateRoles.useMutation();

    // Sync selectedRoles when user data loads
    useEffect(() => {
        if (user) {
            setSelectedRoles(user.adminRoles);
        }
    }, [user]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleAfterOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            void trpcUtils.admin.adminRole.list.invalidate();
            navigate({ to: '/admin-console/user-access/admin-users' });
        }
    };

    const handleSaveRoles = () => {
        updateRoles(
            {
                userId,
                adminRoles: selectedRoles,
            },
            {
                onSuccess: () => {
                    message.success('Admin roles updated successfully');
                    handleClose();
                },
                onError: (err) => {
                    errorMessage.show(err);
                },
            },
        );
    };

    const getDisplayName = () => {
        if (!user) return '';
        return user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.email;
    };

    return (
        <PageController route={Route}>
            <Drawer
                title={
                    <Space>
                        <ShieldCheck className="h-5 w-5" />
                        <span>Edit admin roles</span>
                    </Space>
                }
                open={open}
                onClose={handleClose}
                afterOpenChange={handleAfterOpenChange}
                width={480}
                footer={
                    <Space className="flex justify-end">
                        <Button type="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={handleSaveRoles} loading={isUpdating}>
                            Save changes
                        </Button>
                    </Space>
                }
            >
                <TRPCErrorBoundary error={error}>
                    {isLoading ? (
                        <Skeleton active />
                    ) : user ? (
                        <div className="space-y-6">
                            {/* User Info Header */}
                            <div className="flex items-center gap-3 rounded-lg bg-muted p-4">
                                <Avatar src={user.photoUrl} icon={<User className="h-4 w-4" />} size={48} />
                                <div>
                                    <Typography.Text strong className="block">
                                        {getDisplayName()}
                                    </Typography.Text>
                                    {(user.firstName || user.lastName) && (
                                        <Typography.Text type="secondary" className="text-sm">
                                            {user.email}
                                        </Typography.Text>
                                    )}
                                </div>
                            </div>

                            {/* Role Selection */}
                            <AdminRoleSelector
                                selectedRoles={selectedRoles}
                                onChange={setSelectedRoles}
                                description="Combine roles to match what this admin should control."
                            />
                        </div>
                    ) : null}
                </TRPCErrorBoundary>
            </Drawer>
        </PageController>
    );
}
